import SeeAllButton from "components/buttons/SeeAllButton";
import { Link } from "gatsby";
import HeadingHighlight from "images/homepage/HeadingHighlight";
import React from "react";
import styled from "styled-components";
import { FluidImageData } from "types";
import { useTranslation } from "react-i18next";

const FeaturesContainer = styled.section.attrs({
    className: "container text-center",
})`
    padding-top: 50px;
`;

const Heading = styled.h1`
    color: var(--bs-code-color);
    font-weight: bold;
    max-width: 932px;
    margin: 40px auto 20px auto;
    padding: 0px 0px;
    position: relative;

    @media (min-width: 992px) {
        margin: 80px auto 20px auto;
        font-size: 56px;
        padding: 15px 0px;
    }
`;

const Highlight = styled(HeadingHighlight)`
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    max-height: 100%;
    max-width: 100%;
    z-index: -1;
`;

const SubHeading = styled.p`
    font-size: 17px;
    max-width: 90%;
    margin: 0 auto;
    font-weight: 400;

    @media (min-width: 992px) {
        font-size: 20px;
        max-width: 440px;
    }
`;

interface Feature {
    title: string;
    description: string;
    alt: string;
    image: {
        srcSetWebp: string;
        aspectRatio: string;
    };
}

interface FeaturesProps {
    heading: string;
    features: Feature[];
}

const Picture = styled.picture.attrs({ loading: "lazy" })<{
    aspectRatio: { light: string };
}>`
    position: relative;
    overflow: hidden;
    margin-bottom: -100px;

    @media (max-width: 992px) {
        z-index: -1;
    }

    & > img {
        max-width: 100%;
        width: 416px;
        object-fit: contain;
        aspect-ratio: ${(props) => props.aspectRatio.light} / 1;
        margin-bottom: -50px;
    }

    &:after {
        content: " ";
        position: absolute;
        top: 60%;
        left: 0;
        bottom: 0;
        width: 100%;
        background: linear-gradient(180deg, transparent, var(--bs-body-bg) 70%);
    }
`;

const FeatureContainerLeft = styled.div.attrs({
    className: "row d-flex justify-content-center mt-5",
})`
    margin-bottom: 50px;

    @media (min-width: 992px) {
        margin-bottom: 150px;
        gap: 80px;

        p {
            font-size: 20px;
        }
    }
`;

const FeatureContainerRight = styled.div.attrs({
    className:
        "row d-flex justify-content-center mt-5 flex-md-row flex-column-reverse",
})`
    margin-bottom: 50px;

    @media (min-width: 992px) {
        margin-bottom: 150px;
        gap: 80px;

        p {
            font-size: 20px;
        }
    }
`;

export default function Features({ heading, features }: FeaturesProps) {
    const { t, i18n } = useTranslation();

    return (
        <FeaturesContainer id="features">
            <Heading>{heading}</Heading>
            {features.map((feature, index) => {
                const isEven = index % 2 === 0;
                const FeatureContainer = (
                    isEven ? FeatureContainerLeft : FeatureContainerRight
                ) as React.FC<React.PropsWithChildren<{}>>;

                return (
                    <FeatureContainer key={index}>
                        {isEven && (
                            <div className="col-md-4 d-flex justify-content-md-start justify-content-center">
                                <Picture
                                    aspectRatio={{
                                        light: feature.image.aspectRatio,
                                    }}
                                >
                                    <img
                                        srcSet={feature.image.srcSetWebp}
                                        loading="lazy"
                                        alt={feature.alt}
                                    />
                                </Picture>
                            </div>
                        )}
                        <div className="col-md-4 d-flex justify-content-center flex-column text-md-start text-sm-center">
                            <h2 className="text-md-start text-sm-center">
                                <strong>{feature.title}</strong>
                            </h2>
                            <p className="mt-0">{feature.description}</p>
                        </div>
                        {!isEven && (
                            <div className="col-md-4 d-flex justify-content-md-end justify-content-center">
                                <Picture
                                    aspectRatio={{
                                        light: feature.image.aspectRatio,
                                    }}
                                >
                                    <img
                                        srcSet={feature.image.srcSetWebp}
                                        loading="lazy"
                                        alt={feature.alt}
                                    />
                                </Picture>
                            </div>
                        )}
                    </FeatureContainer>
                );
            })}

            <div className="d-flex justify-content-center">
                <Link
                    to={
                        i18n.language === "en"
                            ? "/features"
                            : `/${i18n.language}/features`
                    }
                    tabIndex={-1}
                >
                    <SeeAllButton>
                        {t("EP_INDEX_FEATURES_SEE_ALL_BUTTON")}
                    </SeeAllButton>
                </Link>
            </div>
        </FeaturesContainer>
    );
}
